import React from 'react';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import CONSTANTS from '../../../../../constants';
import shopRepository from '../../../../../repository/shop/ShopRepository';
import HasPermission from '../../../../../shared/components/User/HasPermission';
import getError from '../../../../../utils/utils';
import styles from './RemoteTabletSettings.module.css';

const RECENT_HEARTBEAT_DAYS = 7;

const SETTING_DETAILS = {
  'be.joyn.merchant.setting.menu_passcode_required': {
    label: 'Request passcode to enter main menu',
    defaultValue: 'false',
  },
  'be.joyn.merchant.setting.loyalty_amount_threshold': {
    label: 'Request passcode above loyalty amount',
    defaultValue: '-1',
  },
  'be.joyn.merchant.setting.loyalty_interval_threshold': {
    label: 'Standard waiting time between scans',
    defaultValue: '3600',
  },
  'be.joyn.merchant.setting.loyalty_edit_amount_passcode_required': {
    label: 'Request passcode to edit loyalty amount',
    defaultValue: 'true',
  },
  'be.joyn.merchant.setting.loyalty_prefill_enabled': {
    label: 'Prefill loyalty amount',
    defaultValue: 'true',
  },
  'be.joyn.merchant.setting.loyalty_prefill_auto_add_timeout': {
    label: 'Auto-add loyalty after prefill timeout',
    defaultValue: '60',
  },
  'be.joyn.merchant.setting.loyalty_success_timeout': {
    label: 'Return to home after successful loyalty',
    defaultValue: '12',
  },
  'be.joyn.merchant.setting.loyalty_allow_reward_redemption': {
    label: 'Allow reward redemption after loyalty',
    defaultValue: 'false',
  },
  'be.joyn.merchant.setting.idle_rewards_screen_enabled': {
    label: 'Show rewards on the idle screen',
    defaultValue: 'false',
  },
  'be.joyn.merchant.setting.sound_enabled': {
    label: 'Sound',
    defaultValue: 'true',
  },
  'be.joyn.merchant.setting.interface_keypad_automatic_comma_enabled': {
    label: 'Keypad automatic comma',
    defaultValue: 'false',
  },
};

const BOOLEAN = 'BOOLEAN';

const settingLabel = (setting) =>
  SETTING_DETAILS[setting.key]?.label || setting.key;

const defaultValue = (setting) => SETTING_DETAILS[setting.key]?.defaultValue;

const configuredValue = (setting, values) => {
  const value = values[setting.key];

  if (value !== undefined && value !== null && value !== '') {
    return value;
  }

  return defaultValue(setting) || '';
};

const hasRemoteValue = (setting, values) => {
  const value = values[setting.key];
  return value !== undefined && value !== null && value !== '';
};

const formatDate = (date) =>
  date ? moment(date).format('DD/MM/YYYY HH:mm') : '-';

const isRecentHeartbeat = (date) => {
  if (!date) {
    return false;
  }

  return moment(date).isSameOrAfter(
    moment().subtract(RECENT_HEARTBEAT_DAYS, 'days'),
  );
};

const optionLabel = (setting, value) => {
  if (setting.type === BOOLEAN) {
    return value === 'true' ? 'Enabled' : 'Disabled';
  }

  switch (setting.key) {
    case 'be.joyn.merchant.setting.loyalty_amount_threshold':
      if (value === '-1') {
        return 'Never';
      }
      if (value === '0') {
        return 'Always';
      }
      return `Above ${value} points`;
    case 'be.joyn.merchant.setting.loyalty_interval_threshold':
      if (value === '-1') {
        return 'Never';
      }
      if (value === '1800') {
        return '30 minutes';
      }
      if (value === '3600') {
        return '60 minutes';
      }
      if (value === '43200') {
        return '12 hours';
      }
      if (value === '86400') {
        return '24 hours';
      }
      return `${value} seconds`;
    case 'be.joyn.merchant.setting.loyalty_prefill_auto_add_timeout':
    case 'be.joyn.merchant.setting.loyalty_success_timeout':
      return value === '-1' ? 'Never' : `${value} seconds`;
    default:
      return value;
  }
};

const statusLabel = (compatibilityStatus, remoteSettingsSupported) => {
  if (remoteSettingsSupported) {
    return 'Remote settings supported';
  }

  switch (compatibilityStatus) {
    case 'UNSUPPORTED_APP_VERSION':
      return 'App version unsupported';
    case 'UNKNOWN_APP_VERSION':
      return 'App version unknown';
    default:
      return 'Remote settings unavailable';
  }
};

const compatibilityMessage = (settings) => {
  if (!settings || settings.remoteSettingsSupported) {
    return null;
  }

  if (settings.compatibilityStatus === 'UNKNOWN_APP_VERSION') {
    return `This tablet has not reported an app version. Remote settings require Joyn Business ${settings.minimumAppVersion} or newer.`;
  }

  return `This tablet is running Joyn Business ${settings.appVersion ||
    '-'}. Remote settings require ${settings.minimumAppVersion} or newer.`;
};

const appUuid = (device) => device.appUuid || '-';

const osVersion = (device) => device.osVersion || '-';

const valuesFromSettings = (settings) =>
  (settings || []).reduce(
    (values, setting) => ({
      ...values,
      [setting.key]: setting.value,
    }),
    {},
  );

function RemoteTabletSettings({ shopId }) {
  const [devices, setDevices] = React.useState([]);
  const [showRecentOnly, setShowRecentOnly] = React.useState(true);
  const [expandedAppUuids, setExpandedAppUuids] = React.useState([]);
  const [settingsByAppUuid, setSettingsByAppUuid] = React.useState({});
  const [loadingDevices, setLoadingDevices] = React.useState(false);
  const [
    loadingSettingsByAppUuid,
    setLoadingSettingsByAppUuid,
  ] = React.useState({});
  const [savingByAppUuid, setSavingByAppUuid] = React.useState({});
  const [lastExpandedAppUuid, setLastExpandedAppUuid] = React.useState('');
  const expandedAppUuidsRef = React.useRef([]);
  const expandedRowRefs = React.useRef({});

  const clearExpandedSettings = React.useCallback(() => {
    expandedAppUuidsRef.current = [];
    setExpandedAppUuids([]);
    setSettingsByAppUuid({});
    setLoadingSettingsByAppUuid({});
    setSavingByAppUuid({});
    setLastExpandedAppUuid('');
  }, []);

  React.useEffect(() => {
    expandedAppUuidsRef.current = expandedAppUuids;
  }, [expandedAppUuids]);

  const setExpandedRowRef = React.useCallback((appUuid, element) => {
    if (!appUuid) {
      return;
    }

    if (element) {
      expandedRowRefs.current[appUuid] = element;
      return;
    }

    delete expandedRowRefs.current[appUuid];
  }, []);

  const applySettings = React.useCallback((appUuid, settings) => {
    setSettingsByAppUuid((currentSettings) => ({
      ...currentSettings,
      [appUuid]: {
        settings,
        values: valuesFromSettings(settings.settings),
        dirtyValues: {},
      },
    }));
  }, []);

  const loadSettings = React.useCallback(
    (appUuid) => {
      if (!appUuid) {
        clearExpandedSettings();
        return;
      }

      setLoadingSettingsByAppUuid((currentLoading) => ({
        ...currentLoading,
        [appUuid]: true,
      }));
      shopRepository
        .getTabletDeviceSettings(shopId, appUuid)
        .then((settings) => applySettings(appUuid, settings))
        .catch((error) =>
          toastr.error('Could not load tablet settings', getError(error)),
        )
        .finally(() =>
          setLoadingSettingsByAppUuid((currentLoading) => ({
            ...currentLoading,
            [appUuid]: false,
          })),
        );
    },
    [shopId, applySettings, clearExpandedSettings],
  );

  const loadDevices = React.useCallback(() => {
    if (!shopId) {
      return;
    }

    const expandedAppUuidsToRefresh = expandedAppUuidsRef.current;
    setLoadingDevices(true);
    shopRepository
      .getTabletDevices(shopId)
      .then((loadedDevices = []) => {
        const loadedAppUuids = loadedDevices.map((device) => device.appUuid);
        const visibleExpandedAppUuids = expandedAppUuidsToRefresh.filter(
          (appUuid) => loadedAppUuids.includes(appUuid),
        );

        setDevices(loadedDevices);
        setExpandedAppUuids(visibleExpandedAppUuids);
        visibleExpandedAppUuids.forEach((appUuid) => loadSettings(appUuid));
      })
      .catch((error) =>
        toastr.error('Could not load tablet devices', getError(error)),
      )
      .finally(() => setLoadingDevices(false));
  }, [shopId, loadSettings]);

  React.useEffect(() => {
    clearExpandedSettings();
    loadDevices();
  }, [shopId, clearExpandedSettings, loadDevices]);

  React.useEffect(() => {
    const expandedRow = expandedRowRefs.current[lastExpandedAppUuid];

    if (
      expandedAppUuids.includes(lastExpandedAppUuid) &&
      typeof expandedRow?.scrollIntoView === 'function'
    ) {
      expandedRow.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [expandedAppUuids, lastExpandedAppUuid]);

  const updateValue = (appUuid, setting, value) => {
    setSettingsByAppUuid((currentSettings) => ({
      ...currentSettings,
      [appUuid]: {
        ...currentSettings[appUuid],
        values: {
          ...currentSettings[appUuid].values,
          [setting.key]: value,
        },
        dirtyValues: {
          ...currentSettings[appUuid].dirtyValues,
          [setting.key]: value,
        },
      },
    }));
  };

  const toggleRecentFilter = (event) => {
    setShowRecentOnly(event.target.checked);
    clearExpandedSettings();
  };

  const toggleSettings = (device) => {
    if (expandedAppUuids.includes(device.appUuid)) {
      setExpandedAppUuids((currentAppUuids) =>
        currentAppUuids.filter((appUuid) => appUuid !== device.appUuid),
      );
      return;
    }

    setExpandedAppUuids((currentAppUuids) => [
      ...currentAppUuids,
      device.appUuid,
    ]);
    setLastExpandedAppUuid(device.appUuid);

    if (!settingsByAppUuid[device.appUuid]) {
      loadSettings(device.appUuid);
    }
  };

  const dirtySettings = (dirtyValues) =>
    Object.keys(dirtyValues).map((key) => ({
      key,
      value: dirtyValues[key],
    }));

  const save = (appUuid) => {
    const rowState = settingsByAppUuid[appUuid];
    const settingsToUpdate = dirtySettings(rowState.dirtyValues);

    setSavingByAppUuid((currentSaving) => ({
      ...currentSaving,
      [appUuid]: true,
    }));
    shopRepository
      .updateTabletDeviceSettings(shopId, appUuid, settingsToUpdate)
      .then((result) => {
        applySettings(appUuid, result.settings);
        toastr.success('Tablet settings updated');
      })
      .catch((error) => {
        const result = error.response?.data;

        if (result?.settings) {
          applySettings(appUuid, result.settings);
        }

        if (result?.message) {
          toastr.warning('Tablet settings not updated', result.message);
        } else {
          toastr.error('Could not update tablet settings', getError(error));
        }
      })
      .finally(() =>
        setSavingByAppUuid((currentSaving) => ({
          ...currentSaving,
          [appUuid]: false,
        })),
      );
  };

  const visibleDevices = showRecentOnly
    ? devices.filter((device) => isRecentHeartbeat(device.lastCheckinDate))
    : devices;

  const renderDeviceSummary = (tabletSettings) => {
    if (!tabletSettings) {
      return null;
    }

    const message = compatibilityMessage(tabletSettings);

    return (
      <>
        {!tabletSettings.remoteSettingsSupported ? (
          <div className={styles.deviceSummary}>
            <Chip
              label={statusLabel(
                tabletSettings.compatibilityStatus,
                tabletSettings.remoteSettingsSupported,
              )}
            />
          </div>
        ) : null}
        {message ? (
          <Typography
            className={styles.compatibilityMessage}
            color="error"
            variant="body2">
            {message}
          </Typography>
        ) : null}
      </>
    );
  };

  const renderBooleanControl = (appUuid, setting, disabled, settingValues) => {
    const value = configuredValue(setting, settingValues);
    const configured = hasRemoteValue(setting, settingValues);

    return (
      <div className={styles.settingControl}>
        <Switch
          checked={value === 'true'}
          color="primary"
          disabled={disabled}
          inputProps={{ 'aria-label': settingLabel(setting) }}
          onChange={(event) =>
            updateValue(
              appUuid,
              setting,
              event.target.checked ? 'true' : 'false',
            )
          }
        />
        <Typography variant="body2">
          {optionLabel(setting, value)}
          {configured ? '' : ' (default)'}
        </Typography>
      </div>
    );
  };

  const renderIntegerControl = (appUuid, setting, disabled, settingValues) => {
    const value = configuredValue(setting, settingValues);
    const configured = hasRemoteValue(setting, settingValues);

    return (
      <div className={styles.settingControl}>
        <FormControl className={styles.settingSelect}>
          <Select
            disabled={disabled}
            inputProps={{ 'aria-label': settingLabel(setting) }}
            onChange={(event) =>
              updateValue(appUuid, setting, event.target.value)
            }
            value={value}>
            {(setting.allowedValues || []).map((allowedValue) => (
              <MenuItem key={allowedValue} value={allowedValue}>
                {optionLabel(setting, allowedValue)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2">{configured ? '' : 'Default'}</Typography>
      </div>
    );
  };

  const renderSettingControl = (appUuid, setting, disabled, settingValues) =>
    setting.type === BOOLEAN
      ? renderBooleanControl(appUuid, setting, disabled, settingValues)
      : renderIntegerControl(appUuid, setting, disabled, settingValues);

  const renderSettings = (appUuid) => {
    if (loadingSettingsByAppUuid[appUuid]) {
      return (
        <div className={styles.loadingRow}>
          <CircularProgress size={24} />
        </div>
      );
    }

    const rowState = settingsByAppUuid[appUuid];
    const tabletSettings = rowState?.settings;

    if (!tabletSettings) {
      return null;
    }

    const saving = !!savingByAppUuid[appUuid];
    const settingValues = rowState.values;
    const pendingDirtySettings = dirtySettings(rowState.dirtyValues);
    const controlsDisabled = saving || !tabletSettings.remoteSettingsSupported;

    return (
      <div className={styles.expandedContent}>
        {renderDeviceSummary(tabletSettings)}
        <Table className={styles.settingsTable} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Setting</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Stored value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tabletSettings.settings || []).map((setting) => {
              const value = configuredValue(setting, settingValues);
              const configured = hasRemoteValue(setting, settingValues);

              return (
                <TableRow key={setting.key}>
                  <TableCell>
                    <Typography className={styles.settingName} variant="body2">
                      {settingLabel(setting)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {renderSettingControl(
                      appUuid,
                      setting,
                      controlsDisabled,
                      settingValues,
                    )}
                  </TableCell>
                  <TableCell>
                    {configured ? optionLabel(setting, value) : 'No Data'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <div className={styles.actions}>
          {pendingDirtySettings.length ? (
            <Typography variant="body2">
              Pending changes: {pendingDirtySettings.length}
            </Typography>
          ) : null}
          <HasPermission permissions={[CONSTANTS.PERMISSIONS.OP_WRITE_SHOPS]}>
            <Button
              color="secondary"
              disabled={controlsDisabled || pendingDirtySettings.length === 0}
              onClick={() => save(appUuid)}
              variant="contained">
              Save tablet settings
            </Button>
          </HasPermission>
        </div>
      </div>
    );
  };

  const renderDeviceRows = () =>
    visibleDevices.map((device) => {
      const expanded = expandedAppUuids.includes(device.appUuid);

      return (
        <React.Fragment key={device.appUuid}>
          <TableRow hover>
            <TableCell className={styles.idCell}>{appUuid(device)}</TableCell>
            <TableCell>{device.os || '-'}</TableCell>
            <TableCell>{device.brand || '-'}</TableCell>
            <TableCell>
              <Typography className={styles.deviceName} variant="body2">
                {device.model || '-'}
              </Typography>
              {device.serialNumber && device.serialNumber !== device.model ? (
                <Typography className={styles.deviceSubtext} variant="caption">
                  Serial: {device.serialNumber}
                </Typography>
              ) : null}
            </TableCell>
            <TableCell>{osVersion(device)}</TableCell>
            <TableCell>{device.appVersion || '-'}</TableCell>
            <TableCell>{formatDate(device.lastCheckinDate)}</TableCell>
            <TableCell className={styles.settingsCell}>
              <Button
                color="secondary"
                disabled={!device.appUuid}
                onClick={() => toggleSettings(device)}
                size="small">
                {expanded ? 'Hide settings' : 'Settings'}
              </Button>
            </TableCell>
          </TableRow>
          {expanded ? (
            <TableRow>
              <TableCell
                className={styles.expandedCell}
                colSpan={8}
                ref={(element) => setExpandedRowRef(device.appUuid, element)}>
                {renderSettings(device.appUuid)}
              </TableCell>
            </TableRow>
          ) : null}
        </React.Fragment>
      );
    });

  const renderBody = () => {
    if (loadingDevices) {
      return <CircularProgress size={24} />;
    }

    if (!devices.length) {
      return (
        <Typography className={styles.emptyState} variant="body2">
          No tablet devices have checked in for this shop.
        </Typography>
      );
    }

    if (!visibleDevices.length) {
      return (
        <Typography className={styles.emptyState} variant="body2">
          No tablet devices have sent a heartbeat in the last 7 days.
        </Typography>
      );
    }

    return (
      <Table className={styles.deviceTable} size="small">
        <TableHead>
          <TableRow>
            <TableCell>App UUID</TableCell>
            <TableCell>OS</TableCell>
            <TableCell>Brand</TableCell>
            <TableCell>Model</TableCell>
            <TableCell>OS version</TableCell>
            <TableCell>APP version</TableCell>
            <TableCell>Last heartbeat</TableCell>
            <TableCell>Settings</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderDeviceRows()}</TableBody>
      </Table>
    );
  };

  return (
    <div className={styles.section}>
      <h3>Remote tablet settings</h3>
      <Paper className={styles.wrapper}>
        <div className={styles.header}>
          <div />
          <div className={styles.headerActions}>
            <FormControlLabel
              control={
                <Switch
                  checked={showRecentOnly}
                  color="primary"
                  onChange={toggleRecentFilter}
                />
              }
              label="Show only devices active in the last 7 days"
            />
            <Button disabled={loadingDevices} onClick={loadDevices}>
              Refresh
            </Button>
          </div>
        </div>
        {renderBody()}
      </Paper>
    </div>
  );
}

RemoteTabletSettings.propTypes = {
  shopId: PropTypes.number.isRequired,
};

export default RemoteTabletSettings;
